import { useCallback, useEffect, useRef, useState } from "react";
import { useAtom } from "jotai";

import { Timestamp } from "@/components/atoms/Timestamp";
import { Duration } from "@/components/atoms/Duration";
import { useClassNames } from "@/utils/classNames";
import { useDragHandler } from "@/utils/hooks/useDragHandler";
import { useVideoPlaybackControl } from "@/utils/hooks/useVideoPlaybackControl";
import { clamp, calculatePercentage } from "@/utils/MathHelper";
import { videoPlayerAtom } from "@/constants/atoms";
import { checkIfMobileDevice } from "@/utils/hooks/useDeviceDetection";

import { VideoProgressBarProps, ProgressBarSize } from "./types";
import styles from "./VideoProgressBar.module.scss";

/** Constants for video seeking tolerance and delays */
const TIME_DIFF_TOLERANCE = 0.5; // seconds - acceptable difference between target and actual seek position
const MOBILE_BUFFER_DELAY = 1000; // ms - delay for mobile browsers to flush stale buffers
const DESKTOP_BUFFER_DELAY = 200; // ms - delay for desktop browsers to flush stale buffers

/**
 * VideoProgressBar component for mobile video player with draggable playhead
 * Displays background track, elapsed progress, and interactive playhead thumb
 * Features live video scrubbing - the video updates in real-time as you drag
 *
 * Reads currentTime and duration from videoPlayerAtom (Jotai state)
 *
 * @param size - Size variant (medium: 4px, large: 6px)
 * @param videoRef - Reference to video element for live scrubbing preview
 * @param onSeek - Callback when user seeks to a new time (final position)
 * @param onScrub - Optional callback when user is scrubbing (live preview)
 * @param isDraggingControlled - Optional parent-controlled dragging state
 * @param onDragStart - Callback when dragging starts
 * @param onDragEnd - Callback when dragging ends
 * @param className - Additional CSS class for styling
 *
 * @returns The VideoProgressBar component
 *
 * @example
 * ```tsx
 * <VideoProgressBar
 *   size={ProgressBarSize.Medium}
 *   videoRef={videoRef}
 *   onSeek={(time) => console.log('Final seek to:', time)}
 *   onScrub={(time) => console.log('Scrubbing at:', time)}
 * />
 * ```
 */
const VideoProgressBar = ({
  size = ProgressBarSize.Medium,
  videoRef,
  onSeek,
  onScrub,
  isDraggingControlled,
  onDragStart,
  onDragEnd,
  className,
}: VideoProgressBarProps) => {
  const progressBarRef = useRef<HTMLDivElement>(null);
  const [videoPlayerState, setVideoPlayerState] = useAtom(videoPlayerAtom);
  const [dragTime, setDragTime] = useState<number | null>(null);
  const [frozenElapsedTime, setFrozenElapsedTime] = useState<number | null>(
    null,
  );

  // Read from atom instead of props
  const currentTime = videoPlayerState.currentTime;
  const duration = videoPlayerState.duration;

  /** Use video playback control hook */
  const { pauseAndStore, resumeIfWasPlaying, seekTo } = useVideoPlaybackControl(
    { videoRef },
  );

  /**
   * Calculate time from position along the progress bar
   * Handles both landscape and portrait orientations
   * In portrait mode, the container is rotated 90deg, so we need to use clientY instead of clientX
   */
  const calculateTimeFromPosition = useCallback(
    (clientX: number, clientY: number): number => {
      if (!progressBarRef.current) return currentTime;

      const rect = progressBarRef.current.getBoundingClientRect();

      /** Detect if we're in portrait mode by checking if viewport height is greater than width */
      const isPortrait = window.innerHeight > window.innerWidth;

      /** In portrait mode, the container is rotated 90deg, so use clientY for horizontal position */
      const clientPosition = isPortrait ? clientY : clientX;
      const rectStart = isPortrait ? rect.top : rect.left;
      const rectSize = isPortrait ? rect.height : rect.width;

      const position = clamp(clientPosition - rectStart, 0, rectSize);
      const percentage = position / rectSize;
      return percentage * duration;
    },
    [duration, currentTime],
  );

  /**
   * Handle drag start
   */
  const handleDragStart = useCallback(
    (clientX: number, clientY: number) => {
      /** Freeze elapsed progress at current position */
      setFrozenElapsedTime(currentTime);
      const time = calculateTimeFromPosition(clientX, clientY);
      setDragTime(time);

      /** Pause video and store playing state */
      pauseAndStore();

      /** Seek to the initial position (important for clicks that don't move) */
      seekTo(time);

      onDragStart?.();
    },
    [
      calculateTimeFromPosition,
      onDragStart,
      currentTime,
      pauseAndStore,
      seekTo,
    ],
  );

  /**
   * Handle drag move
   */
  const handleDragMove = useCallback(
    (clientX: number, clientY: number) => {
      const time = calculateTimeFromPosition(clientX, clientY);
      setDragTime(time);

      /** Update video preview frame during scrubbing */
      seekTo(time);

      onScrub?.(time);
    },
    [calculateTimeFromPosition, seekTo, onScrub],
  );

  /**
   * Handle drag end (also handles clicks - mousedown + mouseup without movement)
   */
  const handleDragEnd = useCallback(() => {
    if (videoRef?.current) {
      const video = videoRef.current;

      /** Validate video duration before proceeding */
      if (
        !isFinite(video.duration) ||
        isNaN(video.duration) ||
        video.duration <= 0
      ) {
        onDragEnd?.();
        return;
      }

      /**
       * Use video.currentTime as the final position since it reflects the last seek during scrubbing
       * This avoids race conditions with dragTime state updates
       */
      const finalTime = video.currentTime;
      const isMobile = checkIfMobileDevice();

      /** Store the seek target in atom to filter stale timeupdate events */
      setVideoPlayerState((prev) => ({
        ...prev,
        lastSeekTarget: finalTime,
      }));

      /** Video is already paused from pauseAndStore(), just set the seek position */
      video.currentTime = finalTime;

      const handleSeeked = () => {
        video.removeEventListener("seeked", handleSeeked);

        const timeDiff = Math.abs(video.currentTime - finalTime);
        if (timeDiff > TIME_DIFF_TOLERANCE) {
          video.currentTime = finalTime;
        }

        /** Mobile browsers need longer delay to flush stale buffers */
        const bufferSettleDelay = isMobile
          ? MOBILE_BUFFER_DELAY
          : DESKTOP_BUFFER_DELAY;

        setTimeout(() => {
          const prePlayDiff = Math.abs(video.currentTime - finalTime);
          if (prePlayDiff > TIME_DIFF_TOLERANCE) {
            video.currentTime = finalTime;
          }

          resumeIfWasPlaying();
          onSeek?.(finalTime);

          /** Unfreeze elapsed time after playback resumes */
          setFrozenElapsedTime(null);

          /** Call onDragEnd callback AFTER seek completes to trigger controls timer */
          onDragEnd?.();
        }, bufferSettleDelay);
      };

      video.addEventListener("seeked", handleSeeked, { once: true });
    } else {
      /** No video ref, just call onDragEnd immediately */
      onDragEnd?.();
    }
  }, [onSeek, onDragEnd, resumeIfWasPlaying, videoRef, setVideoPlayerState]);

  /**
   * Use drag handler hook for mouse/touch interactions
   * This also handles clicks (mousedown + mouseup without movement)
   */
  const {
    isDragging: isInternalDragging,
    onMouseDown,
    onTouchStart,
  } = useDragHandler({
    onDragStart: handleDragStart,
    onDragMove: handleDragMove,
    onDragEnd: handleDragEnd,
  });

  const isDragging = isDraggingControlled ?? isInternalDragging;

  const displayTime = dragTime ?? currentTime;

  /**
   * Elapsed progress shows actual playback progress
   * Stays frozen during and after scrubbing until video resumes
   */
  const elapsedTime =
    frozenElapsedTime !== null ? frozenElapsedTime : currentTime;
  const elapsedProgress = calculatePercentage(elapsedTime, duration);

  /** Playhead progress shows current scrub position */
  const playheadProgress = calculatePercentage(displayTime, duration);

  /**
   * Clear dragTime once currentTime has caught up
   */
  useEffect(() => {
    if (dragTime !== null && !isDragging) {
      if (Math.abs(currentTime - dragTime) < TIME_DIFF_TOLERANCE) {
        setDragTime(null);
      }
    }
  }, [dragTime, isDragging, currentTime]);

  const containerCn = useClassNames(
    styles,
    styles["video-progress-bar"],
    styles[size],
    isDragging && styles["dragging"],
    className,
  );

  const playheadCn = useClassNames(styles, styles["playhead"]);

  return (
    <div className={styles["progress-bar-container"]}>
      {/* Progress bar */}
      <div className={styles["progress-bar-wrapper"]}>
        <div
          ref={progressBarRef}
          className={containerCn}
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
          role="slider"
          aria-label="Video progress"
          aria-valuemin={0}
          aria-valuemax={duration}
          aria-valuenow={displayTime}
          tabIndex={0}
        >
          {/* Background track */}
          <div className={styles["background-track"]} />

          {/* Elapsed progress */}
          <div
            className={styles["elapsed-progress"]}
            style={{ width: `${elapsedProgress}%` }}
          />

          {/* Playhead thumb */}
          <div
            className={playheadCn}
            style={{ left: `${playheadProgress}%` }}
            aria-hidden="true"
          />

          {/* Current time display above scrubber when dragging */}
          {isDragging && (
            <div
              className={styles["timestamp"]}
              style={{ left: `${playheadProgress}%` }}
            >
              <Timestamp currentTime={displayTime} />
            </div>
          )}
        </div>

        {/* Total time display beside progress bar */}
        <div className={styles["duration"]}>
          <Duration duration={duration} />
        </div>
      </div>
    </div>
  );
};

export { VideoProgressBar, ProgressBarSize };
